<template>
    <ClientOnly>
        <div ref="mapWrapperRef">
            <GoogleMap
                :api-key="getMapApiKey()"
                :class="mapClass"
                :center="center"
                :zoom="zoom"
                :styles="googleMapTheme"
                ref="mapRef"
            >
                <Marker
                    v-for="point in storeLocatorPoints"
                    :key="point.locationId"
                    :options="getMarkerOptions(point)"
                    @click="markerClickHandler(point.locationId, point.latitude, point.longitude)"
                >
                    <InfoWindow class="px-4 py-8" ref="infoWindowRef">
                        <div class="text-center">
                            <ShcUiParagraph theme="tertiary" class="font-semibold mb-4">
                                {{ point.title }}
                            </ShcUiParagraph>
                            <ShcUiParagraph theme="tertiary">{{ point.street }},</ShcUiParagraph>
                            <ShcUiParagraph theme="tertiary">{{ point.zip }} {{ point.city }}</ShcUiParagraph>
                            <ShcUiParagraph theme="tertiary">
                                <span v-html="trimBreak(point.description)"></span>
                            </ShcUiParagraph>
                            <ShcUiParagraph theme="tertiary" class="my-4">{{ point.phone }}</ShcUiParagraph>
                            <template v-if="point.openTime">
                                <ShcUiParagraph theme="tertiary">
                                    {{ $t('Bbk.storeLocatorPoint.moFr') }} {{ point.openTime.openMoFr }} -
                                    {{ point.openTime.closeMoFr }}
                                </ShcUiParagraph>
                                <ShcUiParagraph theme="tertiary">
                                    {{ $t('Bbk.storeLocatorPoint.sa') }} {{ point.openTime.openSa }} -
                                    {{ point.openTime.closeSa }}
                                </ShcUiParagraph>
                                <ShcUiParagraph theme="tertiary">
                                    {{ $t('Bbk.storeLocatorPoint.sn') }} {{ point.openTime.openSn }} -
                                    {{ point.openTime.closeSn }}
                                </ShcUiParagraph>
                            </template>
                        </div>
                    </InfoWindow>
                </Marker>
            </GoogleMap>
        </div>

        <template #fallback>
            <div :class="placeHolderClass" />
        </template>
    </ClientOnly>
</template>

<script lang="ts" setup>
    import type { Props } from './props'
    import type { StoreLocatorPoint } from '../../types/storeLocatorPoint'
    import { GoogleMap, Marker, InfoWindow } from 'vue3-google-map'

    const { $bus } = useNuxtApp()
    const props = defineProps<Props>()
    const { googleMapApiKey } = useRuntimeConfig().public
    const infoWindowRef = ref()
    const mapRef = ref()
    const mapWrapperRef = ref<HTMLElement | null>(null)

    let storeLocatorPoints = props?.markerList

    function getMapApiKey(): string {
        return process.env.NODE_ENV === 'production' ? googleMapApiKey : ''
    }

    function markerClickHandler(pointId: string, lat: number, lng: number) {
        closeAllInfoWindow()
        centerMapOnPoint(lat, lng)
    }

    function closeAllInfoWindow() {
        infoWindowRef.value.map((infoWindowInstance: google.maps.InfoWindow) => {
            infoWindowInstance.close()
        })
    }

    function centerMapOnPoint(lat: number, lng: number) {
        mapRef.value.map.zoom = 17
        mapRef.value.map.setCenter({
            lat: lat,
            lng: lng,
        })
    }

    function getMarkerOptions(point: StoreLocatorPoint) {
        return {
            position: {
                lat: point.latitude,
                lng: point.longitude,
            },
            title: point.title,
            icon: { url: getMarkerImageUri() },
        }
    }

    function getMarkerImageUri() {
        return process.env.NODE_ENV === 'production'
            ? `${window.location.protocol}//headless.${window.location.host}/images/punkt.png`
            : '/images/punkt.png'
    }

    function trimBreak(htmlCode: string) {
        return htmlCode.replace('<br />', ' ')
    }

    function scrollViewToMap(element: Ref<HTMLElement | null>) {
        element.value?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }

    $bus.$on('moveMapToPoint', ({ lat, lng }) => {
        centerMapOnPoint(lat, lng)
        scrollViewToMap(mapWrapperRef)
    })
</script>

<style lang="scss">
    .gm-style-iw.gm-style-iw-c {
        @apply rounded-none shadow-none;
    }
</style>
